/**
 * One connected JSON-LD graph per directory page, linked by @id:
 * agency (Organization + ProfessionalService) + WebSite + the page
 * (CollectionPage or ItemPage) + BreadcrumbList + the source Datasets
 * (Companies House register, OpenStreetMap) + FAQPage. The listed companies
 * are HomeAndConstructionBusiness types; the agency never is. A register
 * company's address is its registered office, so it carries no geo point.
 * No ratings, reviews or invented facts. Safe to import anywhere.
 */

import type { Faq } from "@/lib/construction/content";
import type { Listing } from "@/lib/construction/types";

import { SITE } from "@/lib/site";
import { agencyNode, ORGANIZATION_ID, websiteNode, WEBSITE_ID } from "@/lib/schema/agency";
import { companyHref, getRegion } from "@/lib/construction/places";
import { primaryTrade } from "@/lib/construction/trades";

/** `cms` is the inline-edit key ("table:id:column") when the crumb name is a database value. */
export type Crumb = { name: string; href: string; cms?: string };

export const OSM_DATASET_ID = "https://www.openstreetmap.org/#dataset";
export const CH_DATASET_ID = "https://download.companieshouse.gov.uk/en_output.html#dataset";

const abs = (href: string) => `${SITE.url}${href}`;

function datasetNode() {
  return {
    "@type": "Dataset",
    "@id": OSM_DATASET_ID,
    name: "OpenStreetMap",
    description:
      "OpenStreetMap is a free, editable map of the world built by volunteers and published under the Open Database Licence. ConstructionWebDesign.uk uses its UK records of businesses tagged as a construction craft or a construction company office, with their mapped location, phone number and website.",
    url: "https://www.openstreetmap.org",
    license: "https://opendatacommons.org/licenses/odbl/1-0/",
    isAccessibleForFree: true,
    creator: { "@type": "Organization", name: "OpenStreetMap contributors", url: "https://www.openstreetmap.org/copyright" },
  };
}

function registerDatasetNode() {
  return {
    "@type": "Dataset",
    "@id": CH_DATASET_ID,
    name: "Companies House Free Company Data Product",
    description:
      "The Free Company Data Product is a monthly snapshot of basic company data for live companies on the UK register, published by Companies House. It includes each company's name, number, registered office address, status, incorporation date and SIC codes. ConstructionWebDesign.uk uses the active companies with a construction SIC code (41 to 43).",
    url: "https://download.companieshouse.gov.uk/en_output.html",
    isAccessibleForFree: true,
    creator: { "@type": "GovernmentOrganization", name: "Companies House", url: "https://www.gov.uk/government/organisations/companies-house" },
  };
}

function breadcrumbNode(url: string, crumbs: Crumb[]) {
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: abs(c.href) })),
  };
}

function faqNode(url: string, faqs: Faq[]) {
  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
}

function companyRef(l: Listing) {
  const url = abs(companyHref(l.region, l.townSlug, l.slug));
  return { "@type": "ListItem", url, name: l.name };
}

/** Directory, region and town pages. */
export function collectionGraph(input: {
  href: string;
  name: string;
  description: string;
  crumbs: Crumb[];
  listings: Listing[];
  totalListings: number;
  faqs: Faq[];
  about?: { type: "Country" | "AdministrativeArea" | "City"; name: string };
}) {
  const url = abs(input.href);
  const graph: Record<string, unknown>[] = [
    agencyNode(),
    websiteNode(),
    registerDatasetNode(),
    datasetNode(),
    {
      "@type": "CollectionPage",
      "@id": `${url}#webpage`,
      url,
      name: input.name,
      description: input.description,
      inLanguage: "en-GB",
      isPartOf: { "@id": WEBSITE_ID },
      publisher: { "@id": ORGANIZATION_ID },
      breadcrumb: { "@id": `${url}#breadcrumb` },
      isBasedOn: [{ "@id": CH_DATASET_ID }, { "@id": OSM_DATASET_ID }],
      about: input.about ? { "@type": input.about.type, name: input.about.name } : undefined,
      mainEntity: { "@id": `${url}#companies` },
    },
    {
      "@type": "ItemList",
      "@id": `${url}#companies`,
      name: input.name,
      numberOfItems: input.totalListings,
      itemListElement: input.listings.map((l, i) => ({ ...companyRef(l), position: i + 1 })),
    },
    breadcrumbNode(url, input.crumbs),
  ];
  if (input.faqs.length) graph.push(faqNode(url, input.faqs));
  return { "@context": "https://schema.org", "@graph": graph };
}

/** Company detail page: an ItemPage whose main entity is the listed company. */
export function companyGraph(input: { listing: Listing; name: string; description: string; crumbs: Crumb[]; faqs: Faq[] }) {
  const l = input.listing;
  const href = companyHref(l.region, l.townSlug, l.slug);
  const url = abs(href);
  const trade = primaryTrade(l.trades);
  const region = getRegion(l.region);

  const register = l.source === "ch";
  const company: Record<string, unknown> = {
    "@type": trade?.schemaType ?? "HomeAndConstructionBusiness",
    "@id": `${url}#company`,
    name: l.name,
    legalName: l.chName ?? undefined,
    url: l.website ?? undefined,
    telephone: l.phone ?? undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: l.address ?? undefined,
      addressLocality: l.town,
      addressRegion: region?.longName,
      postalCode: l.postcode ?? undefined,
      addressCountry: "GB",
    },
    // A registered office postcode is not a business location, so only mapped businesses carry a point.
    geo: !register && l.lat !== null && l.lng !== null ? { "@type": "GeoCoordinates", latitude: l.lat, longitude: l.lng } : undefined,
    areaServed: { "@type": "City", name: l.town },
    foundingDate: l.chIncorporatedOn ?? undefined,
    identifier: l.companiesHouseNumber
      ? { "@type": "PropertyValue", propertyID: "Companies House company number", value: l.companiesHouseNumber }
      : undefined,
    subjectOf: { "@id": `${url}#webpage` },
  };

  const sources = register ? [CH_DATASET_ID] : l.companiesHouseNumber ? [OSM_DATASET_ID, CH_DATASET_ID] : [OSM_DATASET_ID];
  const graph: Record<string, unknown>[] = [
    agencyNode(),
    websiteNode(),
    ...(sources.includes(CH_DATASET_ID) ? [registerDatasetNode()] : []),
    ...(sources.includes(OSM_DATASET_ID) ? [datasetNode()] : []),
    {
      "@type": "ItemPage",
      "@id": `${url}#webpage`,
      url,
      name: input.name,
      description: input.description,
      inLanguage: "en-GB",
      isPartOf: { "@id": WEBSITE_ID },
      publisher: { "@id": ORGANIZATION_ID },
      breadcrumb: { "@id": `${url}#breadcrumb` },
      isBasedOn: sources.map((id) => ({ "@id": id })),
      mainEntity: { "@id": `${url}#company` },
    },
    company,
    breadcrumbNode(url, input.crumbs),
  ];
  if (input.faqs.length) graph.push(faqNode(url, input.faqs));
  return { "@context": "https://schema.org", "@graph": graph };
}

/** Serialises a graph for a <script type="application/ld+json">, safe against "</script>". */
export function graphHtml(graph: unknown): string {
  return JSON.stringify(graph).replace(/</g, "\\u003c");
}
